import { Coordinate, TileSize } from "game-common/models"
import { ItemTypeCharacteristics } from "game-common/item/item_type_characteristics"
import type { TileMapMetaProvider } from "game-common/tile_map_meta_provider"

/**
 * Laying a wall atop a wall: point at the spot where a wall would stand on top of a wall (a storey up, where it
 * will be drawn) and the new wall goes on the storey above, raising that storey when it does not exist yet.
 * Pointing anywhere else stays on the player's own storey. Like a roof, the cell is picked on its drawn plane,
 * so the cursor and the printed wall sit under the pointer. A roof pointed at the top of a raised storey's walls
 * roofs that storey.
 */

/** A cell to build on, and the storey it lands on when that is not the player's own. */
export type BuildTarget = Coordinate & { level?: number }

export type WallMap = Pick<TileMapMetaProvider, "getMapDeltaAt">

/** The player's storey or one linked above it. */
export interface StoreyView {
    level: number
    map: WallMap
}

/** A target with `level` only when there is one, so a plain cell stays exactly `{ x, y }`. */
export const withLevel = (point: Coordinate, level?: number): BuildTarget =>
    level === undefined ? { x: point.x, y: point.y } : { x: point.x, y: point.y, level }

/** The same cell on the same storey. (util's isEqual compares x and y only.) */
export const sameTarget = (a?: BuildTarget, b?: BuildTarget): boolean =>
    !!a && !!b && a.x === b.x && a.y === b.y && a.level === b.level

/** A built wall stands on this cell: its base (stamped with the cell as parent), or a column piece that blocks. */
export const wallCellAt = (map: WallMap, at: Coordinate): boolean => {
    const delta = map.getMapDeltaAt?.(at.x, at.y, "sprite")
    if (!delta || delta.removed || delta.context?.previewTarget) return false
    const parent = delta.context?.parent
    if (parent && (parent.x !== at.x || parent.y !== at.y) && !delta.blocking) return false
    return ItemTypeCharacteristics[delta.context?.itemType]?.buildType === "wall"
}

/** A built roof covers this cell. */
export const roofCellAt = (map: WallMap, at: Coordinate): boolean => {
    const delta = map.getMapDeltaAt?.(at.x, at.y, "sky")
    if (!delta || delta.removed || delta.context?.previewTarget) return false
    return ItemTypeCharacteristics[delta.context?.itemType]?.buildType === "roof"
}

/** The cell under `world` on a plane drawn `yOffset` from the player's (up is negative). */
export const cellOnPlane = (world: Coordinate, yOffset: number): Coordinate => ({
    x: Math.floor(world.x / TileSize),
    y: Math.floor((world.y - yOffset) / TileSize),
})

/**
 * The raised cell under a pointer at `world`: on each storey's plane from one above the top down (higher storeys
 * are drawn over lower ones), the first cell with a wall on the storey below and none of its own yet.
 * `storeys` are consecutive from the player's own upward; `planeOffset(level)` is how far a storey is drawn from
 * the player's. Undefined: the pointer is over no spot atop a wall.
 */
export const atopWallTarget = (world: Coordinate, storeys: StoreyView[], planeOffset: (level: number) => number):
    BuildTarget | undefined => {
    for (let index = storeys.length; index >= 1; index--) {
        const below = storeys[index - 1]
        const level = below.level + 1
        const cell = cellOnPlane(world, planeOffset(level))
        if (wallCellAt(below.map, cell) && !(storeys[index] && wallCellAt(storeys[index].map, cell))) return withLevel(cell, level)
    }
    return undefined
}

/**
 * A roof for a storey above the player's: on each raised storey's roof plane (its plane, lifted by `roofOffset`), from
 * the top down, the first cell over a wall or roof of that storey, so pointing at the top of a raised wall roofs that
 * storey. Undefined: the roof is for the player's own storey.
 */
export const atopRoofTarget = (world: Coordinate, storeys: StoreyView[], planeOffset: (level: number) => number,
    roofOffset: number): BuildTarget | undefined => {
    for (let index = storeys.length - 1; index >= 1; index--) {
        const { level, map } = storeys[index]
        const cell = cellOnPlane(world, planeOffset(level) + roofOffset)
        if (wallCellAt(map, cell) || roofCellAt(map, cell)) return withLevel(cell, level)
    }
    return undefined
}

/** During a stroke that paints storey `level`: the cell under the pointer on that storey's plane. */
export const storeyStrokeTarget = (world: Coordinate, level: number, planeOffset: (level: number) => number, lift = 0): BuildTarget =>
    withLevel(cellOnPlane(world, planeOffset(level) + lift), level)
